/**
 * Die Schreibwarteschlange -- der **einzige** Weg, auf dem eine Buchung
 * entsteht (CLAUDE.md §3.3).
 *
 * Es gibt in dieser Anwendung keinen Codepfad, der eine Buchung direkt an den
 * Server schickt. Auch nicht „nur eben schnell" (ADR 0004).
 *
 * - Die Buchung bekommt ihre Kennung (UUID v7) **auf dem Geraet** und liegt
 *   sofort in IndexedDB. Ein Neuladen der Seite verliert nichts.
 * - Die Oberflaeche zeigt das Ergebnis aus dem lokalen Stand, nicht erst nach
 *   der Serverantwort (`lokal/bestand.ts` rechnet die Warteschlange mit ein).
 * - Uebertragen wird stapelweise, angestossen vom Abgleich
 *   (`lokal/abgleich.ts`), mit Wiederholung bei Netzfehlern.
 * - Was **nicht** ankommt, verschwindet nicht. Es bleibt sichtbar liegen.
 */

import { v7 as uuidv7 } from "uuid";
import type { Buchung, BuchungAnlegen, BuchungErgebnis } from "@thw/contracts";
import { anfrage } from "./klient.js";
import {
  ablegen,
  alleMit,
  angekommen,
  stapelSchreiben,
  zaehlenMit,
  type WartendeBuchung,
} from "./speicher.js";

export type { WartendeBuchung } from "./speicher.js";

/** Wie oft eine Buchung wiederholt wird, bevor sie als abgelehnt gilt. */
const HOECHSTE_VERSUCHE = 5;

/** Groesse eines Uebertragungsstapels. */
const STAPEL = 100;

/**
 * Laenger wartet niemand auf die Antwort. Kam der Stapel trotzdem an, erkennt
 * der Server ihn beim naechsten Versuch an den Kennungen wieder.
 */
const ZEITLIMIT_MS = 30_000;

/**
 * Wer nach jeder neuen Buchung Bescheid bekommt -- der Abgleich, damit sie
 * sofort losgeht, wenn Netz da ist.
 *
 * Als Anmeldung und nicht als Import: Der Abgleich benutzt die Warteschlange,
 * und in umgekehrter Richtung soll keine zweite Abhaengigkeit entstehen.
 */
const nachBuchung = new Set<() => void>();

export function aufNeueBuchung(tun: () => void): () => void {
  nachBuchung.add(tun);
  return () => nachBuchung.delete(tun);
}

/**
 * Erzeugt die Kennung einer Buchung.
 *
 * UUID v7, weil sie die Entstehungszeit enthaelt und damit sortierbar ist --
 * das erleichtert dem Server das Schreiben und uns das Lesen eines Protokolls.
 */
export function neueBuchungsKennung(): string {
  return uuidv7();
}

/**
 * Nimmt eine Buchung an.
 *
 * Kehrt zurueck, sobald sie **lokal festgeschrieben** ist -- nicht erst, wenn
 * der Server sie hat. Das ist der ganze Punkt: An der Einsatzstelle gibt es
 * kein Netz, und die Rueckmeldung nach einem Scan darf nicht darauf warten.
 *
 * Wirft, wenn das Ablegen scheitert. Der Aufrufer muss das zeigen -- eine
 * Meldung „gebucht", die auch dann erscheint, wenn nichts gespeichert wurde,
 * ist schlimmer als gar keine.
 */
export async function buchen(buchung: BuchungAnlegen): Promise<void> {
  await ablegen({
    id: buchung.id,
    buchung,
    angelegt_am: new Date().toISOString(),
    versuche: 0,
    grund: null,
    zustand: "wartet",
  });
  for (const tun of nachBuchung) tun();
}

/**
 * Schickt, was wartet.
 *
 * Wird nur vom Abgleich aufgerufen, und der laeuft nie zweimal gleichzeitig:
 * Sonst koennte derselbe Stapel doppelt unterwegs sein. Dass der Server das
 * aushielte -- die Kennung kommt vom Geraet --, ist kein Grund, es zu tun.
 *
 * Wirft, wenn der Server nicht erreichbar ist oder den Stapel als Ganzes
 * abweist. Die Buchungen bleiben dann stehen und gehen beim naechsten Mal mit.
 * **Nicht** als abgelehnt zaehlen -- der Fehler liegt nicht an der Buchung.
 *
 * Liefert die Artikel, deren Buchungen angekommen sind.
 */
export async function uebertragen(): Promise<Set<string>> {
  const beruehrt = new Set<string>();

  for (;;) {
    const alle = await alleMit("wartet");
    if (alle.length === 0) break;
    const wartende = alle.slice(0, STAPEL);

    const antwort = await anfrage<{ ergebnisse: BuchungErgebnis[] }>(
      "POST",
      "/api/core/buchungen",
      { buchungen: wartende.map((w) => w.buchung) },
      ZEITLIMIT_MS,
    );

    const nachSchluessel = new Map(antwort.ergebnisse.map((e) => [e.id, e]));
    const erledigt: Buchung[] = [];
    const geaendert: WartendeBuchung[] = [];
    const jetzt = new Date().toISOString();

    for (const wartend of wartende) {
      const ergebnis = nachSchluessel.get(wartend.id);

      // Angelegt oder schon vorhanden -- beides heisst: angekommen. Die
      // Abschrift im Log traegt die Folgenummer des Servers; den genauen
      // Eingangszeitpunkt bringt der naechste Abruf des Logs.
      if (ergebnis?.ergebnis === "angelegt" || ergebnis?.ergebnis === "bereits_vorhanden") {
        erledigt.push({ ...wartend.buchung, folge: ergebnis.folge, empfangen_am: jetzt });
        beruehrt.add(wartend.buchung.artikel_id);
        continue;
      }

      const versuche = wartend.versuche + 1;
      geaendert.push({
        ...wartend,
        versuche,
        grund: ergebnis?.ergebnis === "abgelehnt" ? ergebnis.grund : "keine Rueckmeldung",
        // Eine fachlich abgelehnte Buchung wird durch Wiederholen nicht
        // besser. Sie bleibt sichtbar liegen, damit jemand sie ansieht --
        // verschwinden waere das Schlimmste.
        zustand:
          ergebnis?.ergebnis === "abgelehnt" || versuche >= HOECHSTE_VERSUCHE
            ? "abgelehnt"
            : "wartet",
      });
    }

    if (erledigt.length > 0) await angekommen(erledigt);
    if (geaendert.length > 0) await stapelSchreiben(geaendert);

    // Bleiben nur noch abgelehnte uebrig, ist der Durchlauf zu Ende.
    if ((await zaehlenMit("wartet")) === 0) break;
    // Kam in diesem Stapel nichts an, bringt ein sofortiger zweiter Versuch
    // nichts. Der naechste Abgleich versucht es wieder.
    if (erledigt.length === 0) break;
  }

  return beruehrt;
}

/**
 * Eine wartende Buchung in der Gestalt des Logs.
 *
 * `folge` und `empfangen_am` vergibt sonst der Server. Fuer die Ableitung
 * spielen beide keine Rolle: Sie sortiert nach `erfasst_am`, und das kommt vom
 * Geraet.
 */
export function alsBuchung(w: WartendeBuchung): Buchung {
  return { ...w.buchung, folge: Number.MAX_SAFE_INTEGER, empfangen_am: w.angelegt_am };
}

/**
 * Was noch aussteht. Abgelehnte zaehlen nicht mit: Sie sind nie angekommen
 * und werden es auch nicht, solange niemand hinsieht.
 */
export async function wartende(): Promise<WartendeBuchung[]> {
  return alleMit("wartet");
}

/** Die abgelehnten Buchungen -- fuer die Anzeige „Es hakt hier". */
export function abgelehnte(): Promise<WartendeBuchung[]> {
  return alleMit("abgelehnt");
}

/** Eine abgelehnte Buchung noch einmal versuchen. */
export async function nochmal(id: string): Promise<void> {
  const alle = await alleMit("abgelehnt");
  const eintrag = alle.find((w) => w.id === id);
  if (eintrag === undefined) return;

  await ablegen({ ...eintrag, zustand: "wartet", versuche: 0, grund: null });
  for (const tun of nachBuchung) tun();
}
